import type React from 'react';
import { Suspense, useState } from 'react';

import { isAfterFirstRender } from '@/renderer/utils/clientNavigation';
import { LoadingIcon } from './LoadingIcon';

export function PageSpinner() {
  return (
    <div className="relative h-[stretch] w-[stretch]">
      <span className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2">
        <LoadingIcon isLoading={true} className="size-6 data-[loading=true]:opacity-50" />
      </span>
    </div>
  );
}

export default function PageSuspense({
  children,
  fallback = true,
}: {
  children: (React.ReactNode | JSX.Element)[] | React.ReactNode | JSX.Element;
  fallback?: boolean | (React.ReactNode | JSX.Element)[] | React.ReactNode | JSX.Element;
}) {
  // A page mounted by a client navigation leaves the default spinner boundary out.
  // That navigation renders in a transition (pages/+onRenderClient.ts), and React
  // keeps the previous page on screen only while the suspending content sits under
  // a boundary that is ALREADY on screen. A boundary mounted with the new page is
  // not, so it would swap the old page for this spinner at once. Without it the
  // page suspends to the layout boundary Vike keeps across pages, the old page
  // stays up under the top progress bar, and anything that suspends later still
  // gets this spinner (pages/+Loading.tsx). Fixed for the life of the component,
  // so its children never remount. Custom fallbacks (skeletons) are kept.
  const [skipSpinnerBoundary] = useState(() => fallback === true && isAfterFirstRender());

  if (fallback && !skipSpinnerBoundary) {
    return (
      <Suspense fallback={fallback === true ? <PageSpinner /> : fallback}>{children}</Suspense>
    );
  }

  return children;
}
